import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import {
  taxonomiesService,
  type Taxonomy,
  type TaxonomyWithTerms,
  type CreateTaxonomyDto,
  type UpdateTaxonomyDto,
  type TaxonomyFilters,
  type TermWithTaxonomy,
  type CreateTermDto,
} from '../services/taxonomies.service';
import {
  createOptimisticItem,
  onMutateCreate,
  onMutateDelete,
  onError,
  onSuccessCreate,
  onSettled,
} from '../lib/optimisticHelpers';

// Re-export types for convenience
export type {
  Taxonomy,
  TaxonomyWithTerms,
  CreateTaxonomyDto,
  UpdateTaxonomyDto,
  TaxonomyFilters,
  TermWithTaxonomy,
  CreateTermDto,
};

/**
 * Result interface for useTaxonomies hook
 */
interface UseTaxonomiesResult {
  taxonomies: TaxonomyWithTerms[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createTaxonomy: (data: CreateTaxonomyDto) => Promise<void>;
  updateTaxonomy: (id: string, data: UpdateTaxonomyDto) => Promise<void>;
  deleteTaxonomy: (id: string) => Promise<void>;
  hardDeleteTaxonomy: (id: string) => Promise<void>;
}

/**
 * Custom hook for managing taxonomies with React Query
 *
 * Benefits:
 * - Automatic request deduplication
 * - Intelligent caching (5 min stale time)
 * - Automatic background refetching
 * - Optimistic updates for better UX
 * - Error handling and retry logic
 *
 * @param filters - Optional filters for taxonomies
 */
export const useTaxonomies = (
  filters?: TaxonomyFilters
): UseTaxonomiesResult => {
  const queryClient = useQueryClient();

  // Merge filters with default isActive: true to exclude soft-deleted items
  const mergedFilters: TaxonomyFilters = {
    isActive: true, // Always filter active items by default
    ...filters,
  };

  // Main query for fetching taxonomies
  const {
    data: taxonomies = [],
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.taxonomies.list(mergedFilters),
    queryFn: () => taxonomiesService.getAll(mergedFilters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
  });

  const error = queryError ? (queryError as Error).message : null;

  // Create taxonomy mutation
  const createTaxonomyMutation = useMutation({
    mutationFn: taxonomiesService.create,
    onMutate: async (newTaxonomy) => {
      const optimisticTaxonomy = createOptimisticItem<TaxonomyWithTerms>({
        ...newTaxonomy,
        terms: [],
        isActive: newTaxonomy.isActive ?? true,
        termCount: 0,
      });
      return onMutateCreate(
        queryClient,
        queryKeys.taxonomies.list(filters),
        optimisticTaxonomy
      );
    },
    onError: (_err, _variables, context) => {
      onError(queryClient, queryKeys.taxonomies.list(filters), context);
    },
    onSuccess: (data) => {
      onSuccessCreate(queryClient, queryKeys.taxonomies.list(filters), data);
    },
    onSettled: () => {
      onSettled(queryClient, queryKeys.taxonomies.all);
    },
  });

  // Update taxonomy mutation
  const updateTaxonomyMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTaxonomyDto }) =>
      taxonomiesService.update(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.taxonomies.all });

      const previousData = queryClient.getQueryData<TaxonomyWithTerms[]>(
        queryKeys.taxonomies.list(filters)
      );

      queryClient.setQueryData<TaxonomyWithTerms[]>(
        queryKeys.taxonomies.list(filters),
        (old) =>
          old?.map((taxonomy) =>
            taxonomy.id === id ? { ...taxonomy, ...data } : taxonomy
          ) ?? []
      );

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          queryKeys.taxonomies.list(filters),
          context.previousData
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.taxonomies.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.terms.all });
    },
  });

  // Delete taxonomy mutation (soft delete)
  const deleteTaxonomyMutation = useMutation({
    mutationFn: taxonomiesService.delete,
    onMutate: async (id) => {
      return onMutateDelete(
        queryClient,
        queryKeys.taxonomies.list(filters),
        id
      );
    },
    onError: (_err, _variables, context) => {
      onError(queryClient, queryKeys.taxonomies.list(filters), context);
    },
    onSuccess: () => {
      onSettled(queryClient, queryKeys.taxonomies.all);
      queryClient.invalidateQueries({ queryKey: queryKeys.terms.all });
    },
  });

  // Hard delete taxonomy mutation
  const hardDeleteTaxonomyMutation = useMutation({
    mutationFn: taxonomiesService.hardDelete,
    onMutate: async (id) => {
      return onMutateDelete(
        queryClient,
        queryKeys.taxonomies.list(filters),
        id
      );
    },
    onError: (_err, _variables, context) => {
      onError(queryClient, queryKeys.taxonomies.list(filters), context);
    },
    onSuccess: () => {
      onSettled(queryClient, queryKeys.taxonomies.all);
      queryClient.invalidateQueries({ queryKey: queryKeys.terms.all });
    },
  });

  return {
    taxonomies,
    loading,
    error,
    refetch: async () => {
      await refetch();
    },
    createTaxonomy: async (data: CreateTaxonomyDto) => {
      await createTaxonomyMutation.mutateAsync(data);
    },
    updateTaxonomy: async (id: string, data: UpdateTaxonomyDto) => {
      await updateTaxonomyMutation.mutateAsync({ id, data });
    },
    deleteTaxonomy: async (id: string) => {
      await deleteTaxonomyMutation.mutateAsync(id);
    },
    hardDeleteTaxonomy: async (id: string) => {
      await hardDeleteTaxonomyMutation.mutateAsync(id);
    },
  };
};

/**
 * Hook to fetch a single taxonomy by ID or slug
 */
export const useTaxonomy = (idOrSlug: string) => {
  return useQuery({
    queryKey: queryKeys.taxonomies.detail(idOrSlug),
    queryFn: () => taxonomiesService.getById(idOrSlug),
    enabled: !!idOrSlug,
    staleTime: 5 * 60 * 1000,
  });
};
